import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import {
  Inbox, Mail, MailOpen, Phone, Building2, Clock, Eye, User
} from 'lucide-react';

export const CollaborationInboxPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const fr = language === 'fr';

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/collaboration-requests`);
      setRequests(res.data.requests || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/admin/collaboration-requests/${id}/read`);
      setRequests(prev => prev.map(r => r.id === id ? { ...r, read: true } : r));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error(err); }
  };

  const selectRequest = (req) => {
    setSelected(req);
    if (!req.read) markAsRead(req.id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="collaboration-inbox">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Inbox className="w-6 h-6" /> {fr ? 'Demandes de Collaboration' : 'Collaboration Requests'}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {fr ? 'Messages recus depuis la page publique' : 'Messages received from the public page'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1" data-testid="unread-badge">
            {unreadCount} {fr ? 'non lu(s)' : 'unread'}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-100 dark:border-slate-700">
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">{requests.length} {fr ? 'demande(s)' : 'request(s)'}</p>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-400">{fr ? 'Chargement...' : 'Loading...'}</div>
            ) : requests.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                {fr ? 'Aucune demande' : 'No requests'}
              </div>
            ) : requests.map(req => (
              <button
                key={req.id}
                onClick={() => selectRequest(req)}
                className={`w-full text-left p-4 border-b border-slate-50 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${selected?.id === req.id ? 'bg-blue-50 dark:bg-slate-700' : ''} ${!req.read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                data-testid={`request-${req.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {req.read ? <MailOpen className="w-3.5 h-3.5 text-slate-400" /> : <Mail className="w-3.5 h-3.5 text-blue-600" />}
                  <span className={`text-sm truncate ${!req.read ? 'font-bold text-slate-800 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>{req.name}</span>
                </div>
                <p className="text-xs text-slate-400 truncate">{req.university}</p>
                <p className="text-[10px] text-slate-300 mt-1">{formatDate(req.created_at)}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 p-6">
          {selected ? (
            <div className="space-y-5" data-testid="request-detail">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-slate-400" /> {selected.name}
                  </h2>
                  <p className="text-slate-400 text-sm flex items-center gap-2 mt-1">
                    <Building2 className="w-3.5 h-3.5" /> {selected.university}
                  </p>
                </div>
                <Badge className={selected.read ? 'bg-slate-100 text-slate-500' : 'bg-blue-100 text-blue-700'}>
                  {selected.read ? (fr ? 'Lu' : 'Read') : (fr ? 'Nouveau' : 'New')}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-700 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Email</p>
                    <a href={`mailto:${selected.email}`} className="text-sm text-blue-600 hover:underline">{selected.email}</a>
                  </div>
                </div>
                {selected.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">{fr ? 'Telephone' : 'Phone'}</p>
                      <a href={`tel:${selected.phone}`} className="text-sm text-blue-600 hover:underline">{selected.phone}</a>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Date</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{formatDate(selected.created_at)}</p>
                  </div>
                </div>
              </div>

              {selected.message && (
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Message</p>
                  <div className="bg-white dark:bg-slate-700 rounded-xl p-4 border border-slate-100 dark:border-slate-600">
                    <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <a href={`mailto:${selected.email}`}>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Mail className="w-3.5 h-3.5" /> {fr ? 'Repondre par Email' : 'Reply by Email'}
                  </Button>
                </a>
                {selected.phone && (
                  <a href={`https://wa.me/${selected.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="gap-2 text-green-600 border-green-200 hover:bg-green-50">
                      <Phone className="w-3.5 h-3.5" /> WhatsApp
                    </Button>
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <Inbox className="w-12 h-12 text-slate-200 mb-4" />
              <p className="text-slate-400 text-sm">{fr ? 'Selectionnez une demande pour voir les details' : 'Select a request to view details'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaborationInboxPage;
